import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_errors from '@/locales/keys/i18n_errors.json';
import i18n_page_auth from '@/locales/keys/i18n_page_auth.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { LOCAL__ui_set_boolean } from '@/state/ui/_base/LOCAL__ui_set_boolean';
import { remote_user_auth_magic_link_generate } from '@/state/user/auth/remote_user_auth_magic_link_generate';
import { set_timeout } from '@/utils/utils_client';
import { DTO_RESPONSE__error } from '@shared/network/DTO_RESPONSE__error';
import { DTO_RESPONSE__success } from '@shared/network/DTO_RESPONSE__success';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

interface Props {
  email: string;
}

export const ButtonAuthMagicLink = React.forwardRef<HTMLButtonElement, Props>(({ email }, ref) => {
  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_AUTH);

  const dispatch = use_dispatch();
  const { toast } = useToast();

  const { page_auth_is_loading } = use_slice_ui(['page_auth_is_loading']);
  const [cooldown, set_cooldown] = useState(0);

  const schema_email = z.string().email(t(i18n_errors.auth.email_invalid, { ns: ENUM__i18n_namespace.ERRORS }));
  /*
   * EFFECTS
   */

  use_effect(() => {
    if (cooldown <= 0) return;

    //todo: try replace to interval
    const timeout_id = set_timeout(() => set_cooldown(prev => prev - 1), 1000);

    return () => clearTimeout(timeout_id);
  }, [cooldown]);

  /*
   * EVENTS
   */

  async function onClick() {
    if (page_auth_is_loading || cooldown > 0) return;

    const validation_email = schema_email.safeParse(email);

    let error: string = '';

    if (validation_email.error) {
      error = [...validation_email.error.errors.map(err => err.message)].join('\n');
    }

    if (validation_email.success) {
      set_cooldown(CONSTANTS__client.DURATION_AUTH_MAGIC_LINK_COOLDOWN);

      let action_result = await dispatch(remote_user_auth_magic_link_generate(email));

      if (action_result.payload instanceof DTO_RESPONSE__success) {
        dispatch(LOCAL__ui_set_boolean({ key: 'page_auth_show_magic_link_sent', value: true }));
      } else if (action_result.payload instanceof DTO_RESPONSE__error) {
        toast({
          title: t(i18n_errors.toast_title_response_error, { ns: ENUM__i18n_namespace.ERRORS }),
          description: t(i18n_errors.all_errors[action_result.payload.type]),
        });
      }
    }

    if (error !== '') {
      toast({ title: t(i18n_errors.toast_title_response_error, { ns: ENUM__i18n_namespace.ERRORS }), description: error });
    }
  }

  /*
   * HTML
   */

  return (
    <UI_PRIMITIVE__button
      variant="outline-on-component"
      className="w-full text-bar-foreground"
      disabled={page_auth_is_loading || cooldown > 0}
      {...{ onClick }}
      ref={ref}
    >
      {cooldown > 0
        ? `${t(i18n_page_auth.auth_component.button.get_started_with_magic_link)} (${cooldown}s)`
        : t(i18n_page_auth.auth_component.button.get_started_with_magic_link)}
    </UI_PRIMITIVE__button>
  );
});
ButtonAuthMagicLink.displayName = 'ButtonAuthMagicLink';
